import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { auditService } from '../services/auditService'
import type { AuditEntry, AuditQueryParams } from '../types/audit'
import { formatDate } from '../utils/locale'
import './Audit.css'

export function Audit() {
  const [filters, setFilters] = useState<AuditQueryParams & { performedBy?: string }>({
    limit: 100,
    changeType: undefined,
    startTime: undefined,
    endTime: undefined,
    performedBy: undefined,
  })

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['audit', filters],
    queryFn: () => {
      if (filters.performedBy) {
        return auditService.getAuditHistoryByUser(filters.performedBy!, filters)
      }
      // For general audit view, we need a different endpoint or show message
      // For now, show a message that user filter is required
      return Promise.resolve({ entries: [], count: 0 })
    },
    enabled: !!filters.performedBy, // Only query if user is specified
  })

  const handleExport = () => {
    if (!data?.entries) return

    const csv = auditService.exportAuditHistoryAsCsv(data.entries)
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'create':
        return 'var(--color-success)'
      case 'update':
        return 'var(--color-info)'
      case 'archive':
        return 'var(--color-warning)'
      case 'restore':
        return 'var(--color-success)'
      case 'view':
        return 'var(--color-muted)'
      case 'delete':
        return 'var(--color-danger)'
      default:
        return 'var(--color-text)'
    }
  }

  const isEffectivelyEmpty = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return true
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') return Object.keys(value).length === 0
    return false
  }

  const formatChangeValue = (value: any): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  if (isLoading) {
    return (
      <div className="audit-page">
        <div className="loading">Loading audit history...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="audit-page">
        <div className="error">
          Error loading audit history: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    )
  }

  const entries = data?.entries || []

  return (
    <div className="audit-page">
      <div className="audit-page-header">
        <h1>Audit History</h1>
        <div className="audit-page-actions">
          <button
            type="button"
            onClick={() => refetch()}
            className="btn btn-secondary audit-page-refresh"
            aria-label="Refresh audit history"
            title="Refresh audit history"
            disabled={isFetching || !filters.performedBy}
          >
            ↻
          </button>
          <button
            onClick={handleExport}
            className="btn btn-secondary"
            disabled={entries.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="audit-page-filters">
        <input
          type="text"
          value={filters.performedBy || ''}
          onChange={(e) => setFilters({ ...filters, performedBy: e.target.value || undefined })}
          className="form-control"
          placeholder="User Email or ID"
        />

        <select
          value={filters.changeType || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              changeType: e.target.value ? (e.target.value as AuditEntry['changeType']) : undefined,
            })
          }
          className="form-control"
        >
          <option value="">All Change Types</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="archive">Archive</option>
          <option value="restore">Restore</option>
          <option value="view">View</option>
        </select>

        <input
          type="date"
          value={filters.startTime?.split('T')[0] || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              startTime: e.target.value ? `${e.target.value}T00:00:00Z` : undefined,
            })
          }
          className="form-control"
          placeholder="Start Date"
        />

        <input
          type="date"
          value={filters.endTime?.split('T')[0] || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              endTime: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
            })
          }
          className="form-control"
          placeholder="End Date"
        />
      </div>

      {!filters.performedBy ? (
        <div className="audit-page-empty">
          <p>Please enter a user email or ID to view audit history.</p>
          <p className="audit-page-hint">
            To view audit history for a specific patient, navigate to the patient detail page.
          </p>
        </div>
      ) : entries.length === 0 ? (
        <div className="audit-page-empty">
          <p>No audit history found for the selected filters.</p>
        </div>
      ) : (
        <div className="audit-page-list">
          {entries.map((entry) => (
            <div key={entry.id} className="audit-page-entry">
              <div className="audit-page-entry-header">
                <div className="audit-page-entry-meta">
                  <span
                    className="audit-page-change-type"
                    style={{ color: getChangeTypeColor(entry.changeType) }}
                  >
                    {entry.changeType.toUpperCase()}
                  </span>
                  <span className="audit-page-timestamp">
                    {formatDate(entry.timestamp, 'dd/MM/yyyy HH:mm')}
                  </span>
                  <span className="audit-page-user">
                    {entry.performedByDisplayName || entry.performedBy}
                  </span>
                  <span className="audit-page-patient-id">Patient: {entry.patientId}</span>
                  {entry.reason && (
                    <span className="audit-page-reason">{entry.reason}</span>
                  )}
                </div>
                {entry.metadata.ipAddress && (
                  <span className="audit-page-ip">{entry.metadata.ipAddress}</span>
                )}
              </div>

              {entry.changes.filter(
                (change) =>
                  !(isEffectivelyEmpty(change.previousValue) && isEffectivelyEmpty(change.newValue)),
              ).length > 0 && (
                <div className="audit-page-changes">
                  <h4>Changes:</h4>
                  <table className="audit-page-changes-table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Previous Value</th>
                        <th>New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.changes
                        .filter(
                          (change) =>
                            !(
                              isEffectivelyEmpty(change.previousValue) &&
                              isEffectivelyEmpty(change.newValue)
                            ),
                        )
                        .map((change, idx) => (
                          <tr key={idx}>
                            <td className="audit-page-field">{change.field}</td>
                            <td className="audit-page-old-value">
                              <pre>{formatChangeValue(change.previousValue)}</pre>
                            </td>
                            <td className="audit-page-new-value">
                              <pre>{formatChangeValue(change.newValue)}</pre>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {entry.changes.filter(
                (change) =>
                  !(isEffectivelyEmpty(change.previousValue) && isEffectivelyEmpty(change.newValue)),
              ).length === 0 && (
                <div className="audit-page-no-changes">
                  <p>No field-level changes recorded.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <div className="audit-page-footer">
          <p>Showing {entries.length} audit entries</p>
        </div>
      )}
    </div>
  )
}
